import { NextPage } from 'next'
import Image from 'next/legacy/image'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { staticPath, staticProps } from '../../../../utils/createApolloClient'
import {
  STORE_SONGS_QUERY,
  STORE_SONG_DETAILS_QUERY,
  STORE_ITEM_QUERY
} from '../../../../gql'
import { filterStateToQueryVariables, initalFiltersState } from '../../../songs'
import StoreLayout from '../../components/StoreLayout'
import StoreSongBanner from './components/StoreSongBanner'
import StoreProductTabs, {
  StoreItemTypeEnum
} from '../../products/components/StoreProductTabs'
import TryItBlock from '../../components/TryItBlock'
import artists, { joinedArtistsStr } from '../../../../utils/songs/artists'
import { t } from '../../../../utils'

const StoreSongPlayerDynamic = dynamic(
  () => import('../../../../components/StoreSongPlayer'),
  {
    ssr: false
  }
)

import styles from './_storeSongPage.module.scss'

import { useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useLazyQuery, useMutation } from '@apollo/client'
import { ADD_TO_CART_MUTATION } from '../../../../gql/mutations/add_to_cart_mutation'
import { albumPath } from '../../../../utils/routeHelpers'
import KeyButtons from './components/KeyButtons'
import useInView from 'react-cool-inview'
import { useAuth } from '../../../../utils/auth'
import { ModalsContext } from '../../../../utils/modals'
import { STORE_SHOPPING_CART_QUERY } from '../../../../gql/queries/store_shopping_cart_query'
import { Song, StoreItem } from '../../../../__generated__/graphql'
import { EditButton, IsHiddenTag } from '../components/StoreSong'

interface StoreSongPageProps {
  ssrSong: Song
  ssrStoreItem: StoreItem
}

const initialStoreItemType = StoreItemTypeEnum.MultiTracks

const fakeSamples = [
  {
    id: 1,
    name: 'Sample',
    attachmentUrl: '/samples/Same_God_-_Sample.m4a'
  },
  {
    id: 2,
    name: 'Bright piano',
    attachmentUrl: '/samples/Way_maker_-_bright_piano.m4a'
  },
  {
    id: 3,
    name: 'Organ shimmer pad',
    attachmentUrl: '/samples/Way_maker_-_organ_shimmer_pad.m4a'
  }
]

const StoreSongPage: NextPage<StoreSongPageProps> & {
  theme?: string
} = ({ ssrSong, ssrStoreItem }) => {
  const router = useRouter()
  const { currentUser, isAuthenticated, isAdmin } = useAuth()

  const { id } = router.query

  const [userSong, setUserSong] = useState<Song>(null)
  const [storeItem, setStoreItem] = useState<StoreItem>(null)
  const [isPurchased, setIsPurchased] = useState<boolean>(false)

  const getSong = (): Song => {
    return userSong || ssrSong
  }

  const getStoreItem = (): StoreItem => {
    return storeItem || ssrStoreItem
  }

  const song = getSong()

  const [currentStoreItem, setCurrentStoreItem] =
    useState<StoreItem>(getStoreItem())
  const [currentItemType, setCurrentItemType] = useState<StoreItemTypeEnum>(
    currentStoreItem?.itemType || initialStoreItemType
  )
  const { observe, inView } = useInView({
    onEnter: ({ unobserve }) => unobserve()
  })

  const [queryUserSong] = useLazyQuery(STORE_SONG_DETAILS_QUERY, {
    variables: { id: Number(id) },
    onCompleted: (data) => {
      setUserSong(data.storeSong as Song)
    },
    fetchPolicy: 'no-cache'
  })

  const [getStoreItemRequest] = useLazyQuery(STORE_ITEM_QUERY, {
    variables: { songId: Number(id), storeItemType: currentItemType },
    onCompleted: (data) => {
      if (data.storeItem === null) {
        setCurrentStoreItem({
          description: null,
          id: null,
          itemType: currentItemType,
          memberPrice: null,
          payloads: [],
          price: null,
          samples: [],
          software: null,
          song: song
        } as StoreItem)
      } else {
        setStoreItem(data.storeItem as StoreItem)
        setIsPurchased(data.storeItem.purchased)
      }
    },
    fetchPolicy: 'no-cache'
  })

  useEffect(() => {
    if (id && currentUser) {
      queryUserSong()
      getStoreItemRequest()
    }
  }, [id, currentUser])

  const { openModal, closeModal } = useContext(ModalsContext)

  useEffect(() => {
    const paramsFromQuery = router.query || {}
    const paramsFromQueryTab = paramsFromQuery.tab as StoreItemTypeEnum
    if (
      paramsFromQueryTab &&
      paramsFromQueryTab !== currentStoreItem?.itemType
    ) {
      setCurrentItemType(paramsFromQuery.tab as StoreItemTypeEnum)
      storeItemRequest({
        variables: { songId: song?.id, storeItemType: paramsFromQueryTab }
      })
    }
  }, [router.query])

  const [storeItemRequest] = useLazyQuery(STORE_ITEM_QUERY, {
    onCompleted: (data) => {
      if (!song.visibleInStore && data.storeItem === null) {
        setCurrentStoreItem({
          description: null,
          id: null,
          itemType: currentItemType,
          memberPrice: null,
          payloads: [],
          price: null,
          samples: [],
          software: null,
          song: song
        } as StoreItem)
      } else {
        setCurrentStoreItem(data.storeItem as StoreItem)
        setIsPurchased(data.storeItem.purchased)
      }
    }
  })

  const [fetchCart, { data: cartData }] = useLazyQuery(
    STORE_SHOPPING_CART_QUERY
  )

  useEffect(() => {
    isAuthenticated && fetchCart()
  }, [isAuthenticated])

  const [addToCart] = useMutation(ADD_TO_CART_MUTATION, {
    variables: { itemId: currentStoreItem?.id },
    onCompleted: () => {
      openAddToCartModal()
    },
    refetchQueries: [{ query: STORE_SHOPPING_CART_QUERY }]
  })

  const goToCart = () => router.push('/store/cart')

  const openAddToCartModal = () => {
    openModal({
      name: 'StoreAddToCartModal',
      props: {
        song,
        onContinue: closeModal,
        onViewCart: () => {
          closeModal()
          goToCart()
        }
      },
      modifier: styles.addToCartModalModifier
    })
  }

  const openSignInModal = () => {
    openModal({
      name: 'SignIn',
      props: {
        onClose: closeModal
      },
      modalTitle: t('Sign In')
    })
  }

  const handleAddToCart = () => {
    if (!isAuthenticated) openSignInModal()
    else addToCart()
  }

  const samples = process.env.NEXT_PUBLIC_FAKE_SAMPLES
    ? fakeSamples.slice(0, currentStoreItem?.samples?.length)
    : currentStoreItem?.samples

  const isInCart =
    isAuthenticated &&
    cartData?.storeShoppingCart?.storeItems.some(
      (item) => item.id === currentStoreItem?.id
    )

  if (!song || !currentStoreItem) return <></> // needed for successul build as song can be null on ssr

  const isHidden = !song.visibleInStore

  const payloadsAvailable =
    currentStoreItem.payloads?.filter((p) => p.attachmentUrl).length > 0

  return (
    <StoreLayout
      pageTitle={`${song.title} ${t('by')} ${joinedArtistsStr(
        song.artists
      )} - Worship Online`}
      pageDescription={`${t(
        'Download multitracks, stems, and keyboard patches for'
      )} ${song.title} ${t('by')} ${joinedArtistsStr(song.artists)}.`}
    >
      <StoreSongBanner song={song} />
      <div className={styles.storeSongPage}>
        <div className={classNames('section', styles.songMainSection)}>
          <div className={classNames('container', styles.songMainContainer)}>
            <div className="columns is-7 is-variable">
              <div className="column is-12-mobile is-4-tablet">
                <div className={styles.storeSongImageContainer}>
                  <div className="is-relative">
                    <div
                      className={classNames('is-square', styles.storeSongImage)}
                    >
                      <Image
                        src={song.album.cover.medium}
                        priority={true}
                        alt={`${song.title} cover`}
                        layout={'fill'}
                      />
                    </div>
                    {isAdmin && (
                      <>
                        {isHidden && (
                          <span className={styles.isHiddenTag}>
                            <span>{t('Hidden')}</span>
                            <IsHiddenTag />
                          </span>
                        )}
                        <EditButton songId={song.id} />
                      </>
                    )}
                  </div>
                  <div className={styles.storeSongImagePriceOptions}>
                    <s className={styles.storeSongImageRegularPrice}>
                      {currentStoreItem.price &&
                        `$${currentStoreItem.price.toFixed(2)} ${t(
                          'regular price'
                        )}`}
                    </s>
                    <div className={styles.storeSongImageSalePrice}>
                      {currentStoreItem.memberPrice &&
                        `$${currentStoreItem.memberPrice.toFixed(2)} ${t(
                          'member price'
                        )}`}
                    </div>
                  </div>
                  {isPurchased ? (
                    <button
                      className={classNames(
                        'button is-large is-wide is-grey-disabled is-rectangular',
                        styles.cartButton,
                        styles.isDisabled
                      )}
                    >
                      {t('Purchased')}
                    </button>
                  ) : payloadsAvailable ? (
                    <button
                      className={classNames(
                        'button is-large is-wide is-primary is-rectangular',
                        styles.cartButton
                      )}
                      onClick={isInCart ? goToCart : handleAddToCart}
                    >
                      {isInCart ? t('View Cart') : t('Add to Cart')}
                    </button>
                  ) : (
                    <button
                      className={classNames(
                        'button is-large is-wide is-primary is-rectangular',
                        styles.cartButton,
                        styles.isDisabled
                      )}
                    >
                      {t('Coming soon!')}
                    </button>
                  )}
                </div>
              </div>

              <div
                className={classNames(
                  'column is-12-mobile is-8-tablet',
                  styles.songDetails
                )}
              >
                <h1>{song.title}</h1>
                <div className={styles.songArtistName}>
                  {artists(song)}
                  {` | `}
                  <a href={albumPath(song.album)}>{song.album.name}</a>
                </div>
                <div className={styles.songInfo}>
                  <div>
                    <span>{t('KEY')}</span>
                    <span>{` ${song.key}  |  `}</span>
                  </div>
                  <div>
                    <span>{t('BPM')}</span>
                    <span>{` ${song.bpm}  |  `}</span>
                  </div>
                  <div>
                    <span>{t('TIME SIG')}</span>
                    <span>{` ${song.timeSignature}`}</span>
                  </div>
                </div>
                <div ref={observe}>
                  {inView && (
                    <>
                      {currentItemType != StoreItemTypeEnum.PremiumPatches ? (
                        <>
                          {samples.map((sample) => {
                            if (sample.attachmentUrl) {
                              return (
                                <div
                                  className={styles.storePlayer}
                                  key={sample.id}
                                >
                                  <StoreSongPlayerDynamic
                                    sampleUrl={sample.attachmentUrl}
                                  />
                                </div>
                              )
                            }
                          })}
                          {(currentStoreItem.description ||
                            payloadsAvailable) && (
                            <div
                              className={classNames(
                                'columns',
                                styles.storeSongTracksAndKeys
                              )}
                            >
                              {currentStoreItem.description && (
                                <div
                                  className={classNames(
                                    'column is-6-tablet',
                                    styles.storeSongMultitrackList
                                  )}
                                >
                                  <h2>{t('Track List')}</h2>
                                  {`${currentStoreItem.description}`}
                                </div>
                              )}
                              {payloadsAvailable && (
                                <div
                                  className={classNames(
                                    'column is-6-tablet',
                                    styles.storeSongAvailableKeys
                                  )}
                                >
                                  <h2>{t('Available Keys')}</h2>
                                  <ul
                                    className={
                                      'columns is-multiline is-2 is-variable'
                                    }
                                  >
                                    <KeyButtons storeItem={currentStoreItem} />
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={styles.storePatchesBlock}>
                          {currentStoreItem.description && (
                            <div
                              className={classNames(
                                styles.storePatchesDescription
                              )}
                            >
                              {currentStoreItem.description?.replace(
                                /\r\n\r\n/g,
                                '\n'
                              )}
                            </div>
                          )}
                          {currentStoreItem.software && (
                            <div
                              className={
                                currentStoreItem.description
                                  ? styles.storeSoftware
                                  : styles.storeSoftwareStandalone
                              }
                            >
                              <span>Software required: </span>
                              {currentStoreItem.software}
                            </div>
                          )}

                          {samples.map((sample) => {
                            return (
                              <div
                                className={styles.storeSongSample}
                                key={sample.id}
                              >
                                <div
                                  className={classNames(
                                    styles.storePatchSampleName
                                  )}
                                >
                                  {sample.name}
                                </div>
                                <div
                                  className={styles.storePatchSampleDescription}
                                >
                                  {sample.description}
                                </div>
                                {sample.attachmentUrl && (
                                  <StoreSongPlayerDynamic
                                    sampleUrl={sample.attachmentUrl}
                                  />
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {inView && (
          <>
            <StoreProductTabs tab={currentItemType} song={song} />
            <TryItBlock />
          </>
        )}
      </div>
    </StoreLayout>
  )
}

export const getStaticPaths = staticPath({
  additionalPaths: async ({ client }) => {
    const { data } = await client.query({
      query: STORE_SONGS_QUERY,
      variables: filterStateToQueryVariables(initalFiltersState)
    })

    const paths = data.storeSongs.map((song) => ({
      params: { id: song.id.toString() }
    }))

    return {
      paths,
      fallback: true
    }
  }
})

export const getStaticProps = staticProps({
  additionalProps: async ({ client, ctx }) => {
    const [{ data: songData }, { data: storeItemData }] = await Promise.all([
      client.query({
        query: STORE_SONG_DETAILS_QUERY,
        variables: { id: Number(ctx.params.id) }
      }),
      client.query({
        query: STORE_ITEM_QUERY,
        variables: {
          songId: Number(ctx.params.id),
          storeItemType: initialStoreItemType
        }
      })
    ])

    const song = songData?.storeSong || null
    const storeItem = storeItemData?.storeItem || null
    return {
      ssrSong: song,
      ssrStoreItem: storeItem || null
    }
  }
})
StoreSongPage.theme = 'dark'
export default StoreSongPage
